import {Action, ReducerBuilder} from "redux-ts";
import {Reducer} from "platform/redux/Reducer";
import Platform from "platform/Platform";
import {ServiceType} from "enum/ServiceType";
import {DepositReduxState, PackageMultiStep} from "core/redux/deposit/DepositReduxState";
import DepositEngine from "core/engine/DepositEngine";
import {
    DoSubmitDepositCCType,
    SetCreditCardProvider,
    SetCreditCardProviderPayload,
    SetCreditCardValid,
    SetCreditCardValidPayload,
    SetDepositAmount,
    SetDepositConfiguration,
    SetDepositConfigurationPayload,
    SetPaymentMethod,
    SetPaymentMethodPayload,
    SetDepositCurrency,
    CurrencyPayload,
    SelectBankCurrency,
    SelectBankCurrencyPayload,
    SelectBank,
    SelectBankPayload,
    TryChangeCurrencyType,
    SetReceiptDetails,
    SetReceiptDetailsPayload,
    DoSubmitDepositAPMType,
    SetDepositAmountError,
    SetDepositAmountErrorPayload,
    ValidateDepositAmountType,
    MergeDepositFormPayload,
    MergeClientDetails,
    DoSubmitDepositCC,
    DoSubmitDepositCCPayload,
    SetSelectedPaymentProvider,
    SetSelectedPaymentProviderPayload,
    SetBriteAccessToken,
    DoVerifyDepositAPMType,
    UpdateDepositConfiguration,
    UpdateDepositConfigurationPayload,
    DoFetchAPMBanksType,
    SetAPMBanks,
    SetAPMBanksPayload,
    SetEduTradingPackage,
    SetEduTradingPackagePayload,
    SetPropTradingPackages,
    SetPropTradingPackagesPayload,
    SetPropTradingActivePackage,
    SetPropTradingActivePackagePayload,
    SetPropTradingActivePackageType,
    SetPropTradingAddOns, SetPropTradingAddOnsPayload, SetPropTradingActiveAddOn, SetPropTradingActiveAddOnPayload,
    SetPropTradingStep, SetPropTradingStepPayload,
    UpdatePropTradingPackages, UpdatePropTradingPackagesPayload,
    SetIsPropTradingMultiStep, SetIsPropTradingMultiStepPayload,
    SetDiscount, SetDiscountPayload,
    CalculateDiscountType,
    SetPackagePriceConverted,
    SetPackagePriceConvertedPayload,
    ConvertPackagePriceType,
} from "core/redux/deposit/DepositReduxActions";
import {PaymentMethod} from "enum/PaymentMethod";
import Utils from "platform/util/Utils";
import {Currency} from "platform/enum/Currency";
import {TSMap} from "typescript-map";
import {SetAppReadyType} from "platform/redux/core/CoreActions";
import {
    BankDetail,
    ClientDetail,
    DepositCurrency,
    PaymentProvider
} from "protocol/response/GetDepositConfigurationsResponse";
import {BooleanPayload, NumberPayload, StringPayload} from "core/redux/StoreActions";
import {FieldType} from "enum/FieldType";
import {DepositUtil} from "core/util/DepositUtil";
import {PackagesMultiStepType} from "enum/PackagesMultiStepType";
import {PackageStepStatus} from "enum/PackageStepStatus";

export const DEF_CREDIT_CARD_LENGTH: number = 20;

const propTradingSteps = new TSMap<PackagesMultiStepType, PackageMultiStep>();
propTradingSteps.set(PackagesMultiStepType.SelectPackage, {step: PackagesMultiStepType.SelectPackage, status: PackageStepStatus.NotPassed});
propTradingSteps.set(PackagesMultiStepType.ChooseAddons, {step: PackagesMultiStepType.ChooseAddons, status: PackageStepStatus.NotPassed});
propTradingSteps.set(PackagesMultiStepType.Checkout, {step: PackagesMultiStepType.Checkout, status: PackageStepStatus.NotPassed});

export default class DepositReducer extends Reducer<DepositReduxState> {

    private static _instance: DepositReducer;

    public static instance(): DepositReducer {
        return this._instance || (this._instance = new this());
    }

    private constructor() {
        super();
        const depositEngine: DepositEngine = Platform.engine(ServiceType.Deposit);
        this._middlewareActions.set(SetAppReadyType, depositEngine.onAppReady);
        this._middlewareActions.set(TryChangeCurrencyType, depositEngine.onTryChangeCurrency);
        this._middlewareActions.set(DoSubmitDepositCCType, depositEngine.doSubmitDepositCC);
        this._middlewareActions.set(DoSubmitDepositAPMType, depositEngine.doSubmitDepositAPM);
        this._middlewareActions.set(ValidateDepositAmountType, depositEngine.doValidatePaymentAmount);
        this._middlewareActions.set(DoVerifyDepositAPMType, depositEngine.doVerifyDepositAPM);
        this._middlewareActions.set(DoFetchAPMBanksType, depositEngine.doFetchAPMBanks);
        this._middlewareActions.set(SetPropTradingActivePackageType, depositEngine.doGetPropTradingAddOns);
        this._middlewareActions.set(CalculateDiscountType, depositEngine.doCalculateDiscount);
        this._middlewareActions.set(ConvertPackagePriceType, depositEngine.onConvertChangePackageCurrency);
    }

    public get name(): string {
        return "deposit";
    }

    protected setup(builder: ReducerBuilder<DepositReduxState>): void {
        builder
            .init({
                APMBanks: [],
                Currencies: new TSMap<Currency, DepositCurrency>(),
                PaymentMethod: PaymentMethod.CreditCard,
                PaymentMethods: [PaymentMethod.CreditCard],
                PaymentProviders: new TSMap<number, PaymentProvider>(),
                PaymentProviderGroups: new TSMap<string, PaymentProvider[]>(),
                PaymentProviderIdPerEWalletId: new TSMap<string, number>(),
                SelectedPaymentProvider: new TSMap<string | number, PaymentProvider>(),
                supportedCreditCards: [],
                Form: {},
                maxCardLength: DEF_CREDIT_CARD_LENGTH,
                Client: {},
                BanksCurrencies: [],
                Banks: new TSMap<Currency, BankDetail[]>(),
                BankSelection: new TSMap<Currency, BankDetail>(),
                ActivePropTradingAddons: [],
                PropTradingPackages: [],
                PropTradingAddons: [],
                PropTradingStep: PackagesMultiStepType.SelectPackage,
                PropTradingSteps: propTradingSteps,
                PackagePriceConverted: new TSMap<number, number>(),
            })
            .handle(SetDepositConfiguration, (state: DepositReduxState, {payload}: Action<SetDepositConfigurationPayload>) => {
                this._logger.debug("Set deposit configuration.");
                const {
                    CreditCards,
                    Currencies,
                    PaymentMethods,
                    PaymentProviders,
                    PaymentProviderGroups,
                    PaymentProviderIdPerEWalletId,
                    BanksCurrencies,
                    Banks,
                    BankSelection,
                    Client,
                    PlatformUrl,
                    ChargeInDifferentCurrencyDisclaimer
                } = payload;
                return Object.assign({}, state, {
                    PaymentMethod: PaymentMethods[0],
                    PaymentMethods,
                    PaymentProviders,
                    PaymentProviderGroups,
                    PaymentProviderIdPerEWalletId,
                    Currencies,
                    supportedCreditCards: CreditCards,
                    Form: {
                        ...state.Form,
                        cardName: `${Utils.nullToEmpty(Client?.FirstName)} ${Utils.nullToEmpty(Client?.LastName)}`
                    },
                    Client: payload.Client,
                    BanksCurrencies,
                    Banks,
                    BankSelection,
                    BankCurrency: BanksCurrencies[0],
                    PlatformUrl,
                    ChargeInDifferentCurrencyDisclaimer
                });
            })
            .handle(UpdateDepositConfiguration, (state: DepositReduxState, {payload}: Action<UpdateDepositConfigurationPayload>) => {
                const {Currencies,} = payload;
                return Object.assign({}, state, {
                    Currencies
                });
            })
            .handle(SetDepositAmount, (state: DepositReduxState, {payload}: Action<NumberPayload>) => {
                this._logger.debug("Set deposit Amount: " + payload.value);
                return Object.assign({}, state, {
                    Amount: payload.value
                });
            })
            .handle(SetDepositAmountError, (state: DepositReduxState, {payload}: Action<SetDepositAmountErrorPayload>) => {
                return Object.assign({}, state, {
                    AmountError: payload.error
                });
            })
            .handle(SetDepositCurrency, (state: DepositReduxState, {payload}: Action<CurrencyPayload>) => {
                this._logger.debug("Set selected currency: " + payload.currency);
                return Object.assign({}, state, {
                    SelectedCurrency: payload.currency
                });
            })
            .handle(SetSelectedPaymentProvider, (state: DepositReduxState, {payload}: Action<SetSelectedPaymentProviderPayload>) => {
                const newState: DepositReduxState = Utils.merge({}, state);
                newState.SelectedPaymentProvider.set(payload.paymentMethod, payload.provider);
                if (!DepositUtil.isBrite(newState.PaymentProviders.get(payload.paymentMethod))) {
                    newState.BriteAccessToken = null;
                }
                return newState;
            })
            .handle(SetPaymentMethod, (state: DepositReduxState, {payload}: Action<SetPaymentMethodPayload>) => {
                this._logger.debug("Set payment method: " + payload.paymentMethod);
                const newState: DepositReduxState = Object.assign({}, state, {
                    PaymentMethod: payload.paymentMethod
                });
                if (!DepositUtil.isBrite(newState.PaymentProviders.get(payload.paymentMethod))) {
                    newState.BriteAccessToken = null;
                }
                return newState;
            })
            .handle(SetCreditCardProvider, (state: DepositReduxState, {payload}: Action<SetCreditCardProviderPayload>) => {
                this._logger.debug("Set credit card provider: " + payload.creditCardType + " Max card length: " + payload.maxCardLength);
                return Object.assign({}, state, {
                    creditCardType: payload.creditCardType,
                    maxCardLength: payload.maxCardLength || DEF_CREDIT_CARD_LENGTH
                });
            })
            .handle(DoSubmitDepositCC, (state: DepositReduxState, {payload}: Action<DoSubmitDepositCCPayload>) => {
                const Form: {[key: string]: string} = Utils.merge({}, state.Form);
                Form[FieldType.CardName] = payload.form[FieldType.CardName];
                return Object.assign({}, state, {
                    Form
                });
            })
            .handle(MergeClientDetails, (state: DepositReduxState, {payload}: Action<MergeDepositFormPayload>) => {
                const Client: ClientDetail = {...state.Client};
                Client.Address = payload.form[FieldType.Address];
                Client.City = payload.form[FieldType.City];
                Client.ZipCode = payload.form[FieldType.ZipCode];
                return Object.assign({}, state, {
                    Client
                });
            })
            .handle(SetCreditCardValid, (state: DepositReduxState, {payload}: Action<SetCreditCardValidPayload>) => {
                return Object.assign({}, state, {
                    creditCardValid: payload.valid
                });
            })
            .handle(SelectBankCurrency, (state: DepositReduxState, {payload}: Action<SelectBankCurrencyPayload>) => {
                return Object.assign({}, state, {
                    BankCurrency: payload.currency
                });
            })
            .handle(SelectBank, (state: DepositReduxState, {payload}: Action<SelectBankPayload>) => {
                const newState: DepositReduxState = Utils.merge({}, state);
                newState.BankSelection.set(payload.bank.AssetName, payload.bank);
                return newState;
            })
            .handle(SetReceiptDetails, (state: DepositReduxState, {payload}: Action<SetReceiptDetailsPayload>) => {
                return Object.assign({}, state, {
                    CreditCardReceiptDetails: payload.CreditCardReceiptDetails,
                    APMReceiptDetails: payload.APMReceiptDetails
                });
            })
            .handle(SetBriteAccessToken, (state: DepositReduxState, {payload}: Action<StringPayload>) => {
                return Object.assign({}, state, {
                    BriteAccessToken: payload.value
                });
            })
            .handle(SetAPMBanks, (state: DepositReduxState, {payload}: Action<SetAPMBanksPayload>) => {
                return Object.assign({}, state, {
                    APMBanks: payload.Banks
                });
            })
            .handle(SetEduTradingPackage, (state: DepositReduxState, {payload}: Action<SetEduTradingPackagePayload>) => {
                return Object.assign({}, state, {
                    EduPackage: payload.Package,
                });
            })
            .handle(SetPropTradingPackages, (state: DepositReduxState, {payload}: Action<SetPropTradingPackagesPayload>) => {
                return Object.assign({}, state, {
                    PropTradingPackages: payload.packages
                });
            })
            .handle(SetPropTradingActivePackage, (state: DepositReduxState, {payload}: Action<SetPropTradingActivePackagePayload>) => {
                return Object.assign({}, state, {
                    ActivePropTradingPackage: payload.ptPackage,
                    ActivePropTradingAddons: []
                });
            })
            .handle(SetPropTradingAddOns, (state: DepositReduxState, {payload}: Action<SetPropTradingAddOnsPayload>) => {
                return Object.assign({}, state, {
                    PropTradingAddons: payload.addOns
                });
            })
            .handle(SetPropTradingActiveAddOn, (state: DepositReduxState, {payload}: Action<SetPropTradingActiveAddOnPayload>) => {
                const newState: DepositReduxState = Utils.merge({}, state);
                newState.ActivePropTradingAddons = [...state.ActivePropTradingAddons];
                if (payload.active) {
                    if (newState.ActivePropTradingAddons.indexOf(payload.addOn.Id) < 0) {
                        newState.ActivePropTradingAddons.push(payload.addOn.Id);
                    }
                } else {
                    Utils.remove(newState.ActivePropTradingAddons, payload.addOn.Id);
                }
                return newState;
            })
            .handle(SetPropTradingStep, (state: DepositReduxState, {payload}: Action<SetPropTradingStepPayload>) => {
                return Object.assign({}, state, {
                    PropTradingStep: payload.step
                });
            })
            .handle(UpdatePropTradingPackages, (state: DepositReduxState, {payload}: Action<UpdatePropTradingPackagesPayload>) => {
                const newState: DepositReduxState = Utils.merge({}, state);
                const newStepDetails: PackageMultiStep = {step: payload.step, status: PackageStepStatus.Passed};
                newState.PropTradingSteps.set(payload.step, newStepDetails);
                return newState;
            })
            .handle(SetIsPropTradingMultiStep, (state: DepositReduxState, {payload}: Action<SetIsPropTradingMultiStepPayload>) => {
                return Object.assign({}, state, {
                    IsPropTradingMultiStep: payload.value,
                });
            })
            .handle(SetDiscount, (state: DepositReduxState, {payload}: Action<SetDiscountPayload>) => {
                return Object.assign({}, state, {
                    DiscountedAmount: payload.DiscountedAmount,
                    AppliedDiscount: payload.Discount
                });
            })
            .handle(SetPackagePriceConverted, (state: DepositReduxState, {payload}: Action<SetPackagePriceConvertedPayload>) => {
                const newState: DepositReduxState = Utils.merge({}, state);
                newState.PackagePriceConverted.set(payload.packageId, payload.amount);
                return newState;
            });
    }
}
